import { useFlags } from "../../services/utilities/launch_darkly_helper";
import useUserAccount from "./use_user_account";
import { ApiUserPermission, UserPermissionAction, UserPermissionType } from "avvir";
import { isAnyPermissionImplied, UserPermissionLike } from "../../services/user_permission_service";
import { useMemo } from "react";
import useAvvirSelector from "../../services/component_helpers/component_effects/use_avvir_selector";
import getOrganizationId from "../../services/getters/organization_getters/get_organization_id";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import { useUserPermissions } from "./use_user_permissions";

export class PermissionService {
  permissions: ApiUserPermission[];
  enabled: boolean;
  currentOrganizationId: string;
  currentProjectId: string;

  constructor(
    permissions: ApiUserPermission[],
    enabled: boolean,
    currentOrganizationId: string,
    currentProjectId: string
  ) {
    this.permissions = permissions;
    this.enabled = enabled;
    this.currentOrganizationId = currentOrganizationId;
    this.currentProjectId = currentProjectId;
  }

  isPermitted(query: UserPermissionLike, fallback: boolean = true, forceEnableCheck: boolean = false): boolean {
    if (!forceEnableCheck && !this.enabled) {
      return fallback;
    }

    return isAnyPermissionImplied(this.permissions, query);
  }

  isPermittedForCurrentProject(action: UserPermissionAction, fallback?: boolean, forceEnableCheck?: boolean): boolean {
    return this.isPermitted({
      firebaseOrganizationId: this.currentOrganizationId,
      firebaseProjectId: this.currentProjectId,
      permissionType: UserPermissionType.PROJECT,
      permissionAction: action
    }, fallback, forceEnableCheck);
  }

  areAnyPermittedForCurrentOrganization(actions: UserPermissionAction[], fallback?: boolean, forceEnableCheck?: boolean): boolean {
    for (const act of actions) {
      if (this.isPermittedForCurrentOrganization(act, fallback, forceEnableCheck)) {
        return true;
      }
    }
    return false;
  }

  isPermittedForCurrentOrganization(action: UserPermissionAction, fallback?: boolean, forceEnableCheck?: boolean): boolean {
    return this.isPermitted({
      firebaseOrganizationId: this.currentOrganizationId,
      firebaseProjectId: this.currentProjectId,
      permissionType: UserPermissionType.ORGANIZATION,
      permissionAction: action
    }, fallback, forceEnableCheck);
  }
}

export function usePermissionService() {
  const { enableRoleBasedPermissionChecking } = useFlags();
  const permissions = useUserPermissions();
  const organizationId = useAvvirSelector(getOrganizationId);
  const projectId = useAvvirSelector(getProjectId);

  return useMemo(() => {
    return new PermissionService(permissions, enableRoleBasedPermissionChecking,
      organizationId, projectId);
  }, [enableRoleBasedPermissionChecking, organizationId, permissions, projectId]);
}
